import {
  readWorkflowPublishedInspection,
  type WorkflowPublishedInspection,
  type WorkflowPublishedInspectionSource,
  type WorkflowSavedPresence,
} from './workflow-published-inspection';
import type {
  WorkflowPublishedWorkFact,
  WorkflowPublishedWorkDetailRow,
  WorkflowPublishedWorkDetailGroup,
  WorkflowPublishedWorkRow,
  WorkflowPublishedWorkState,
  WorkflowPublishedWorkInspection,
} from './workflow-published-work-inspection';

export type WorkflowPublishedPresentationSectionId = 'staffPage' | 'dashboards';
export type WorkflowPublishedPresentationState = WorkflowPublishedWorkState;
export type WorkflowPublishedPresentationFact = WorkflowPublishedWorkFact;
export type WorkflowPublishedPresentationDetailRow =
  WorkflowPublishedWorkDetailRow;
export type WorkflowPublishedPresentationDetailGroup =
  WorkflowPublishedWorkDetailGroup;
export type WorkflowPublishedPresentationRow = WorkflowPublishedWorkRow;
export interface WorkflowPublishedPresentationSection {
  id: WorkflowPublishedPresentationSectionId;
  label: string;
  state: WorkflowPublishedPresentationState;
  presence: WorkflowSavedPresence;
  fields: readonly WorkflowPublishedPresentationFact[];
  groups: readonly WorkflowPublishedPresentationDetailGroup[];
  rows: readonly WorkflowPublishedPresentationRow[];
  issues: readonly string[];
  raw: unknown;
}
export interface WorkflowPublishedPresentationInspection {
  context: string;
  state: 'available' | 'unavailable';
  provenance: WorkflowPublishedInspection['provenance'];
  issues: readonly string[];
  target: { nodeIndex: number; nodeId: string | null };
  node: WorkflowPublishedWorkInspection['node'];
  sections: Readonly<
    Record<
      WorkflowPublishedPresentationSectionId,
      WorkflowPublishedPresentationSection
    >
  >;
  pins: WorkflowPublishedPresentationDetailGroup | null;
}

type Saved = Record<string, unknown>;
type Field = readonly [string, string];
const record = (value: unknown): value is Saved =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const object = (value: unknown): Saved => (record(value) ? value : {});
const present = (owner: Saved, key: string): WorkflowSavedPresence =>
  !Object.hasOwn(owner, key)
    ? 'absent'
    : owner[key] === null
      ? 'null'
      : 'value';
const copy = (value: unknown): unknown => structuredClone(value);
const text = (value: unknown): value is string =>
  typeof value === 'string' && !!value.trim();
const positive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
const literal = (value: unknown, allowed: readonly string[]): boolean =>
  typeof value === 'string' && allowed.includes(value);
const state = (issues: readonly string[]): 'available' | 'retained-unknown' =>
  issues.length ? 'retained-unknown' : 'available';
function display(value: unknown): string {
  if (value === undefined) return 'Not declared';
  if (value === null) return 'Explicitly null';
  if (typeof value === 'string')
    return value === '' ? 'Empty saved text' : value;
  return JSON.stringify(value) ?? 'Unsupported saved value';
}
function facts(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedPresentationFact[] {
  const value = object(raw);
  return fields.map(([key, label]) => ({
    id: `${prefix}:${key}`,
    label,
    value: display(value[key]),
    presence: present(value, key),
    raw: copy(value[key]),
    issues: [],
  }));
}
function extras(
  raw: unknown,
  fields: readonly Field[],
  prefix: string,
): WorkflowPublishedPresentationFact[] {
  const known = new Set(fields.map(([key]) => key));
  return facts(
    raw,
    Object.keys(object(raw))
      .filter((key) => !known.has(key))
      .map((key) => [key, `Saved ${key}`] as const),
    prefix,
  );
}
function extraIssues(raw: unknown, keys: readonly string[]): string[] {
  return Object.keys(object(raw)).some((key) => !keys.includes(key))
    ? [
        'Additional saved properties are retained without interpreting their meaning.',
      ]
    : [];
}
function row(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  fields: readonly Field[],
  issues: readonly string[] = [],
): WorkflowPublishedPresentationDetailRow {
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: facts(raw, fields, `${prefix}-${index}`),
    issues: [
      ...(!record(raw)
        ? [
            'This saved entry has an unsupported shape. Its original value is retained.',
          ]
        : []),
      ...issues,
    ],
    raw: copy(raw),
  };
}
function collection(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  project: (
    raw: unknown,
    index: number,
    all: readonly unknown[],
  ) => WorkflowPublishedPresentationDetailRow,
): WorkflowPublishedPresentationDetailGroup {
  const raw = owner[key],
    presence = present(owner, key),
    rows = Array.isArray(raw)
      ? raw.map((item, index) => project(item, index, raw))
      : [];
  const issues =
    presence === 'value' && !Array.isArray(raw)
      ? [
          'This saved collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : issues.length || rows.some((item) => item.issues.length)
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
function properties(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
): WorkflowPublishedPresentationDetailGroup {
  const raw = owner[key],
    presence = present(owner, key),
    value = object(raw);
  const issues =
    presence === 'value' && !record(raw)
      ? [
          'This saved object has an unsupported shape. Its original value is retained.',
        ]
      : [];
  const rows = Object.keys(value).map((name, index) => ({
    id: `${prefix}:${key}-${index}`,
    index,
    label: name,
    fields: [
      {
        id: `${prefix}:${key}-${index}:value`,
        label: 'Saved value',
        value: display(value[name]),
        presence: 'value' as const,
        issues: [],
        raw: copy(value[name]),
      },
    ],
    issues: [],
    raw: copy(value[name]),
  }));
  return {
    id: `${prefix}:${key}`,
    label,
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : issues.length
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    rows,
    issues,
    raw: copy(raw),
  };
}
function ids(
  raw: unknown,
  all: readonly unknown[],
  key: string,
  label: string,
): string[] {
  const value = object(raw)[key];
  return !text(value)
    ? [`The saved ${label} is missing or has an unsupported shape.`]
    : all.filter((item) => object(item)[key] === value).length !== 1
      ? [
          `Duplicate saved ${label}: ${value}. Every indexed declaration is retained; no first match is substituted.`,
        ]
      : [];
}
function scalarRows(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
): WorkflowPublishedPresentationDetailGroup {
  return collection(owner, key, label, prefix, (raw, index, all) => ({
    id: `${prefix}:${key}-${index}`,
    index,
    label: `Saved value ${index + 1}`,
    fields: [
      {
        id: `${prefix}:${key}-${index}:value`,
        label: 'Value',
        value: display(raw),
        presence: 'value',
        issues: [],
        raw: copy(raw),
      },
    ],
    issues: [
      ...(!text(raw) ? ['This saved value is not nonempty text.'] : []),
      ...(text(raw) && all.filter((item) => item === raw).length !== 1
        ? [
            `Duplicate saved value: ${raw}. Its order and occurrences are retained.`,
          ]
        : []),
    ],
    raw: copy(raw),
  }));
}
function referenceRow(
  raw: unknown,
  index: number,
  prefix: string,
  label: string,
  kind?: 'view' | 'command',
): WorkflowPublishedPresentationDetailRow {
  const value = object(raw),
    key = object(value['key']);
  const fields = facts(
    value,
    [
      ['key', 'Capability key'],
      ['inputSchemaHash', 'Saved input schema hash'],
      ['outputSchemaHash', 'Saved output schema hash'],
      ['contractHash', 'Saved contract hash'],
    ],
    `${prefix}-${index}`,
  );
  fields.splice(
    1,
    0,
    ...facts(
      key,
      [
        ['provider', 'Provider'],
        ['kind', 'Kind'],
        ['code', 'Code'],
        ['version', 'Version'],
      ],
      `${prefix}-${index}:key`,
    ),
  );
  const issues =
    !record(raw) ||
    !record(value['key']) ||
    !['provider', 'kind', 'code'].every((name) => text(key[name])) ||
    !positive(key['version']) ||
    !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
      text(value[name]),
    )
      ? [
          'This saved capability reference has an unsupported shape. Its exact original value is retained.',
        ]
      : [];
  if (kind && key['kind'] !== kind)
    issues.push(`This saved reference does not declare kind ${kind}.`);
  issues.push(
    ...extraIssues(value, [
      'key',
      'inputSchemaHash',
      'outputSchemaHash',
      'contractHash',
    ]),
    ...extraIssues(key, ['provider', 'kind', 'code', 'version']),
  );
  return {
    id: `${prefix}-${index}`,
    index,
    label,
    fields: [
      ...fields,
      ...extras(
        value,
        [
          ['key', ''],
          ['inputSchemaHash', ''],
          ['outputSchemaHash', ''],
          ['contractHash', ''],
        ],
        `${prefix}-${index}`,
      ),
    ],
    issues,
    raw: copy(raw),
  };
}
function referenceGroup(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  kind?: 'view' | 'command',
): WorkflowPublishedPresentationDetailGroup {
  const presence = present(owner, key),
    raw = owner[key];
  const rows =
    presence === 'value'
      ? [referenceRow(raw, 0, `${prefix}:${key}`, label, kind)]
      : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : state(rows.flatMap((item) => item.issues)),
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function referenceSignature(raw: unknown): string | null {
  const value = object(raw),
    key = object(value['key']);
  if (
    !['provider', 'kind', 'code'].every((name) => text(key[name])) ||
    !positive(key['version']) ||
    !['inputSchemaHash', 'outputSchemaHash', 'contractHash'].every((name) =>
      text(value[name]),
    )
  )
    return null;
  return JSON.stringify([
    key['provider'],
    key['kind'],
    key['code'],
    key['version'],
    value['inputSchemaHash'],
    value['outputSchemaHash'],
    value['contractHash'],
  ]);
}
function referenceKeySignature(raw: unknown): string | null {
  const key = object(object(raw)['key']);
  return ['provider', 'kind', 'code'].every((name) => text(key[name])) &&
    positive(key['version'])
    ? JSON.stringify([
        key['provider'],
        key['kind'],
        key['code'],
        key['version'],
      ])
    : null;
}
const detailFields: readonly Field[] = [
  ['layout', 'Saved layout'],
  ['contextPlacement', 'Reference content placement'],
  ['formTabId', 'Form tab ID'],
  ['mainInteraction', 'Saved main interaction'],
];
const blockFields: readonly Field[] = [
  ['instanceId', 'Content ID'],
  ['provider', 'Provider'],
  ['code', 'Code'],
  ['version', 'Version'],
  ['settingsVersion', 'Settings version'],
  ['region', 'Region'],
  ['width', 'Width'],
  ['tabId', 'Tab ID'],
];
const dashboardFields: readonly Field[] = [
  ['id', 'Dashboard ID'],
  ['label', 'Dashboard name'],
  ['scope', 'Scope'],
  ['queueScope', 'Receiving queue restriction'],
  ['workflowStatusCodes', 'Included workflow statuses'],
  ['stepStatusCodes', 'Included step labels'],
  ['audienceRoleCodes', 'Audience roles'],
  ['presentation', 'Saved presentation'],
];
function tabIssue(id: unknown, detail: Saved, uniqueNode: boolean): string[] {
  if (id === undefined) return [];
  if (!text(id))
    return ['The explicit saved tab membership has an unsupported shape.'];
  if (!uniqueNode)
    return [
      'Saved tab cross-references are not resolved because the step ID is missing or duplicated.',
    ];
  const tabs = detail['tabs'];
  return Array.isArray(tabs) &&
    tabs.filter((tab) => object(tab)['id'] === id).length === 1
    ? []
    : [
        `The saved tab ID ${id} does not identify exactly one declared tab. No default tab is substituted.`,
      ];
}
function staffPage(
  declaration: Saved,
  uniqueNode: boolean,
): WorkflowPublishedPresentationSection {
  const raw = declaration['detail'],
    presence = present(declaration, 'detail'),
    detail = object(raw);
  const issues =
    presence === 'value' && !record(raw)
      ? [
          'The saved Staff page declaration has an unsupported shape. Its original value is retained.',
        ]
      : [];
  if (record(raw) && detail['layout'] !== 'workspace.two-column.v1')
    issues.push(
      'The saved layout is unavailable to this inspector. Its values are shown without rendering or substituting a page.',
    );
  if (
    record(raw) &&
    !literal(detail['contextPlacement'], ['side', 'below', 'hidden'])
  )
    issues.push(
      'The saved reference placement is not a supported literal. No placement default is inferred.',
    );
  if (Object.hasOwn(detail, 'formTabId'))
    issues.push(...tabIssue(detail['formTabId'], detail, uniqueNode));
  issues.push(
    ...extraIssues(detail, [
      'layout',
      'contextPlacement',
      'tabs',
      'formTabId',
      'blocks',
      'mainInteraction',
    ]),
  );
  const tabs = collection(
    detail,
    'tabs',
    'Saved page tabs',
    'staff',
    (item, index, all) =>
      row(
        item,
        index,
        'staff-tab',
        text(object(item)['label'])
          ? String(object(item)['label'])
          : `Saved tab ${index + 1}`,
        [
          ['id', 'Tab ID'],
          ['label', 'Tab name'],
        ],
        [
          ...ids(item, all, 'id', 'tab ID'),
          ...(!text(object(item)['label'])
            ? ['The saved tab name has an unsupported shape.']
            : []),
          ...extraIssues(item, ['id', 'label']),
        ],
      ),
  );
  const mainRaw = detail['mainInteraction'],
    main = object(mainRaw),
    mainPresence = present(detail, 'mainInteraction');
  const mainRows =
    mainPresence === 'value'
      ? [
          row(
            mainRaw,
            0,
            'staff-main-interaction',
            'Saved main interaction',
            [
              ['code', 'Code'],
              ['version', 'Version'],
            ],
            [
              ...(!text(main['code']) || !positive(main['version'])
                ? ['The saved interaction identity is unsupported.']
                : []),
              ...extraIssues(main, ['code', 'version', 'command']),
            ],
          ),
          referenceRow(
            main['command'],
            0,
            'staff-main-command',
            'Saved main command',
            'command',
          ),
        ]
      : [];
  if (mainPresence === 'value') {
    const commands = object(declaration['behavior'])['commands'],
      signature = referenceSignature(main['command']);
    if (
      !uniqueNode ||
      !signature ||
      !Array.isArray(commands) ||
      commands.filter(
        (command) =>
          referenceSignature(object(command)['capability']) === signature,
      ).length !== 1
    )
      mainRows[0].issues = [
        ...mainRows[0].issues,
        'This saved interaction does not resolve to one command on the exact unique step. Its original binding is retained; no form is inferred.',
      ];
  }
  const mainGroup: WorkflowPublishedPresentationDetailGroup = {
    id: 'staff:mainInteraction',
    label: 'Saved main interaction',
    presence: mainPresence,
    state:
      mainPresence !== 'value'
        ? 'absent'
        : state(mainRows.flatMap((item) => item.issues)),
    rows: mainRows,
    issues: [],
    raw: copy(mainRaw),
  };
  const blocks = detail['blocks'];
  const rows: WorkflowPublishedPresentationRow[] = Array.isArray(blocks)
    ? blocks.map((item, index): WorkflowPublishedPresentationRow => {
        const value = object(item),
          entryIssues = [
            ...ids(item, blocks, 'instanceId', 'content ID'),
            ...(!text(value['provider']) ||
            !text(value['code']) ||
            !positive(value['version']) ||
            !positive(value['settingsVersion'])
              ? [
                  'The saved content contract identity has an unsupported shape.',
                ]
              : []),
            ...(!literal(value['region'], ['main', 'supporting']) ||
            !literal(value['width'], ['full', 'half'])
              ? [
                  'The saved content region or width is unsupported; its original values are retained.',
                ]
              : []),
            ...tabIssue(value['tabId'], detail, uniqueNode),
            ...extraIssues(value, [
              ...blockFields.map(([key]) => key),
              'settings',
            ]),
          ];
        const base = row(
          item,
          index,
          'staff-block',
          text(value['instanceId'])
            ? String(value['instanceId'])
            : `Saved content ${index + 1}`,
          blockFields,
          entryIssues,
        );
        const settings = properties(
          value,
          'settings',
          'Saved content settings',
          `staff-block-${index}`,
        );
        return {
          ...base,
          description:
            'Saved content declaration; provider execution and current availability are not evaluated.',
          state:
            base.issues.length || settings.state === 'retained-unknown'
              ? 'retained-unknown'
              : 'available',
          groups: [settings],
        };
      })
    : [];
  if (
    Object.hasOwn(detail, 'blocks') &&
    blocks !== null &&
    !Array.isArray(blocks)
  )
    issues.push(
      'The saved content collection has an unsupported shape. Its original value is retained.',
    );
  const groups = [tabs, mainGroup];
  return {
    id: 'staffPage',
    label: 'Staff page',
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : issues.length ||
            groups.some((group) => group.state === 'retained-unknown') ||
            rows.some((item) => item.state === 'retained-unknown')
          ? 'retained-unknown'
          : 'available',
    fields: facts(detail, detailFields, 'staff').map((field) =>
      field.id === 'staff:mainInteraction' && field.presence === 'value'
        ? { ...field, value: 'Declared · inspect the saved binding below' }
        : field,
    ),
    groups,
    rows,
    issues,
    raw: copy(raw),
  };
}
function presentationGroups(
  dashboard: Saved,
  prefix: string,
): WorkflowPublishedPresentationDetailGroup[] {
  const raw = dashboard['presentation'],
    value = object(raw);
  const declaration = referenceFreeObject(
    dashboard,
    'presentation',
    'Saved presentation declaration',
    prefix,
    [['version', 'Presentation version']],
  );
  if (present(dashboard, 'presentation') !== 'value') return [declaration];
  declaration.rows = declaration.rows.map((item) => ({
    ...item,
    fields: facts(
      raw,
      [['version', 'Presentation version']],
      `${prefix}:presentation`,
    ),
  }));
  declaration.issues = [
    ...declaration.issues,
    ...extraIssues(raw, [
      'version',
      'tiles',
      'filters',
      'columns',
      'workflowStatusPriorityCodes',
    ]),
  ];
  if (!record(raw) || value['version'] !== 1)
    declaration.issues = [
      ...declaration.issues,
      'This saved presentation version/shape is not interpreted as version 1. Original values are retained.',
    ];
  declaration.state = declaration.issues.length
    ? 'retained-unknown'
    : declaration.state;
  if (!record(raw) || value['version'] !== 1) return [declaration];
  const familyPrefix = `${prefix}:presentation`;
  const tiles = collection(
    value,
    'tiles',
    'Saved summary tiles',
    familyPrefix,
    (item, index, all) => {
      const data = object(item),
        issues = [...ids(item, all, 'id', 'tile ID')];
      if (
        !text(data['label']) ||
        typeof data['filterOnClick'] !== 'boolean' ||
        !literal(data['tone'], [
          'neutral',
          'info',
          'success',
          'warning',
          'danger',
        ])
      )
        issues.push(
          'The saved tile name, tone or filtering value is unsupported.',
        );
      if (
        data['statusScope'] != null &&
        !literal(data['statusScope'], [
          'progress',
          'workflow',
          'step',
          'stepProgress',
          'assignment',
        ])
      )
        issues.push(
          'The saved count scope is unsupported. Its raw meaning is retained.',
        );
      if (data['status'] != null && typeof data['status'] !== 'string')
        issues.push('The saved count value has an unsupported shape.');
      issues.push(
        ...extraIssues(data, [
          'id',
          'label',
          'statusScope',
          'status',
          'tone',
          'filterOnClick',
        ]),
      );
      return row(
        item,
        index,
        `${prefix}:tile`,
        text(data['label']) ? String(data['label']) : `Saved tile ${index + 1}`,
        [
          ['id', 'Tile ID'],
          ['label', 'Name'],
          ['statusScope', 'Count scope'],
          ['status', 'Count'],
          ['tone', 'Tone'],
          ['filterOnClick', 'Filter when selected'],
        ],
        issues,
      );
    },
  );
  const filters = collection(
    value,
    'filters',
    'Saved staff filters',
    familyPrefix,
    (item, index, all) => {
      const data = object(item),
        issues = [...ids(item, all, 'field', 'filter field')];
      if (!text(data['label']) || typeof data['initialValue'] !== 'string')
        issues.push('The saved filter name or starting value is unsupported.');
      if (
        !literal(data['field'], [
          'search',
          'status',
          'history',
          'workflowStatus',
          'stepStatus',
          'stepProgress',
          'assignment',
        ])
      )
        issues.push(
          'The saved staff filter field is unsupported; its original declaration is retained.',
        );
      if (
        data['allowedValues'] != null &&
        (!Array.isArray(data['allowedValues']) ||
          data['allowedValues'].some((item) => typeof item !== 'string'))
      )
        issues.push('Saved allowed choices have an unsupported shape.');
      issues.push(
        ...extraIssues(data, [
          'field',
          'label',
          'initialValue',
          'allowedValues',
        ]),
      );
      return row(
        item,
        index,
        `${prefix}:filter`,
        text(data['label'])
          ? String(data['label'])
          : `Saved filter ${index + 1}`,
        [
          ['field', 'Field'],
          ['label', 'Name'],
          ['initialValue', 'Authored starting value'],
          ['allowedValues', 'Allowed choices'],
        ],
        issues,
      );
    },
  );
  const columns = collection(
    value,
    'columns',
    'Saved presentation columns',
    familyPrefix,
    (item, index, all) =>
      row(
        item,
        index,
        `${prefix}:column`,
        text(object(item)['label'])
          ? String(object(item)['label'])
          : `Saved column ${index + 1}`,
        [
          ['field', 'Field'],
          ['label', 'Name'],
        ],
        [
          ...ids(item, all, 'field', 'column field'),
          ...(!text(object(item)['label'])
            ? ['The saved column name has an unsupported shape.']
            : []),
          ...extraIssues(item, ['field', 'label']),
        ],
      ),
  );
  return [
    declaration,
    tiles,
    filters,
    columns,
    scalarRows(
      value,
      'workflowStatusPriorityCodes',
      'Saved workflow-status priority',
      familyPrefix,
    ),
  ];
}
function referenceFreeObject(
  owner: Saved,
  key: string,
  label: string,
  prefix: string,
  fields: readonly Field[],
): WorkflowPublishedPresentationDetailGroup {
  const raw = owner[key],
    presence = present(owner, key),
    rows =
      presence === 'value'
        ? [row(raw, 0, `${prefix}:${key}`, label, fields)]
        : [];
  return {
    id: `${prefix}:${key}`,
    label,
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : state(rows.flatMap((item) => item.issues)),
    rows,
    issues: [],
    raw: copy(raw),
  };
}
function dashboards(declaration: Saved): WorkflowPublishedPresentationSection {
  const raw = declaration['dashboards'],
    presence = present(declaration, 'dashboards');
  const issues =
    presence === 'value' && !Array.isArray(raw)
      ? [
          'The saved Dashboard collection has an unsupported shape. Its original value is retained.',
        ]
      : [];
  const rows: WorkflowPublishedPresentationRow[] = Array.isArray(raw)
    ? raw.map((item, index): WorkflowPublishedPresentationRow => {
        const value = object(item),
          prefix = `dashboard-${index}`,
          entryIssues = [
            ...ids(item, raw, 'id', 'Dashboard ID'),
            ...(!text(value['label']) || !text(value['scope'])
              ? ['The saved Dashboard name or scope has an unsupported shape.']
              : []),
            ...extraIssues(value, [
              ...dashboardFields.map(([key]) => key),
              'view',
              'defaults',
              'columns',
              'filterFields',
              'summaries',
              'quickActions',
            ]),
          ];
        const base = row(
          item,
          index,
          'dashboard',
          text(value['label'])
            ? String(value['label'])
            : `Saved Dashboard ${index + 1}`,
          dashboardFields,
          entryIssues,
        );
        base.fields = base.fields.map((field) =>
          field.id === `${prefix}:presentation` && field.presence === 'value'
            ? {
                ...field,
                value: 'Declared · inspect saved version and elements below',
              }
            : field,
        );
        const groups = [
          referenceGroup(value, 'view', 'Saved exact source', prefix, 'view'),
          properties(value, 'defaults', 'Saved source defaults', prefix),
          ...(
            [
              'workflowStatusCodes',
              'stepStatusCodes',
              'audienceRoleCodes',
            ] as const
          ).map((key) =>
            scalarRows(
              value,
              key,
              key === 'workflowStatusCodes'
                ? 'Included workflow statuses'
                : key === 'stepStatusCodes'
                  ? 'Included step labels'
                  : 'Saved audience roles',
              prefix,
            ),
          ),
          scalarRows(value, 'columns', 'Saved declared columns', prefix),
          scalarRows(
            value,
            'filterFields',
            'Saved declared filter fields',
            prefix,
          ),
          scalarRows(value, 'summaries', 'Saved declared summaries', prefix),
          collection(
            value,
            'quickActions',
            'Saved quick actions',
            prefix,
            (action, actionIndex) =>
              referenceRow(
                action,
                actionIndex,
                `${prefix}:action`,
                `Saved action ${actionIndex + 1}`,
                'command',
              ),
          ),
          ...presentationGroups(value, prefix),
        ];
        return {
          ...base,
          description:
            'Saved authored Dashboard; no source defaults, staff permissions or runtime counts are inferred.',
          state:
            base.issues.length ||
            groups.some((group) => group.state === 'retained-unknown')
              ? 'retained-unknown'
              : 'available',
          groups,
        };
      })
    : [];
  return {
    id: 'dashboards',
    label: 'Dashboards',
    presence,
    state:
      presence !== 'value'
        ? 'absent'
        : issues.length ||
            rows.some((item) => item.state === 'retained-unknown')
          ? 'retained-unknown'
          : rows.length
            ? 'available'
            : 'empty',
    fields: [],
    groups: [],
    rows,
    issues,
    raw: copy(raw),
  };
}

/** Canonical admission is reported by the server. This reader only clones saved facts. */
export function readWorkflowPublishedPresentationInspection(
  source: WorkflowPublishedInspectionSource,
  target: { nodeIndex: number; nodeId: string | null },
): WorkflowPublishedPresentationInspection {
  const admitted = readWorkflowPublishedInspection(source);
  const unavailable = (
    id: WorkflowPublishedPresentationSectionId,
  ): WorkflowPublishedPresentationSection => ({
    id,
    label: id === 'staffPage' ? 'Staff page' : 'Dashboards',
    state: 'unavailable',
    presence: 'absent',
    fields: [],
    groups: [],
    rows: [],
    issues: [],
    raw: null,
  });
  const sections: Record<
    WorkflowPublishedPresentationSectionId,
    WorkflowPublishedPresentationSection
  > = {
    staffPage: unavailable('staffPage'),
    dashboards: unavailable('dashboards'),
  };
  const result: WorkflowPublishedPresentationInspection = {
    context: JSON.stringify([admitted.context, target]),
    state: 'unavailable',
    provenance: structuredClone(admitted.provenance),
    issues: [...admitted.issues],
    target: { nodeIndex: target.nodeIndex, nodeId: target.nodeId },
    node: null,
    sections,
    pins: null,
  };
  if (admitted.state !== 'available') {
    result.issues = [
      ...admitted.issues,
      'Saved Staff page and Dashboard content is withheld because the canonical document was not admitted.',
    ];
    return result;
  }
  const document = object(source.definitionDocument),
    nodes = document['nodes'];
  if (
    !Number.isSafeInteger(target.nodeIndex) ||
    target.nodeIndex < 0 ||
    !Array.isArray(nodes) ||
    target.nodeIndex >= nodes.length
  ) {
    result.issues = [
      'The exact saved node index is unavailable. No other step is substituted.',
    ];
    return result;
  }
  const rawNode = nodes[target.nodeIndex],
    node = object(rawNode),
    id = typeof node['id'] === 'string' ? node['id'] : null;
  if (
    !record(rawNode) ||
    id !== target.nodeId ||
    node['typeCode'] !== 'human_step'
  ) {
    result.issues = [
      'The saved human-step index and identity do not match. Presentation content is withheld.',
    ];
    return result;
  }
  const uniqueNode =
    text(id) && nodes.filter((item) => object(item)['id'] === id).length === 1;
  result.issues = uniqueNode
    ? []
    : [
        'The saved step identity is missing or duplicated. Indexed facts are retained; cross-references are not guessed.',
      ];
  result.state = 'available';
  result.context = JSON.stringify([admitted.context, target, document]);
  result.node = {
    index: target.nodeIndex,
    id,
    label:
      typeof node['displayName'] === 'string'
        ? display(node['displayName'])
        : `Saved step ${target.nodeIndex + 1}`,
    typeCode: 'human_step',
    issues: [...result.issues],
    raw: copy(rawNode),
  };
  const declaration = node['declaration'];
  if (declaration != null && !record(declaration)) {
    for (const key of ['staffPage', 'dashboards'] as const)
      sections[key] = {
        ...sections[key],
        state: 'retained-unknown',
        presence: 'value',
        issues: [
          'The saved declaration has an unsupported shape. Its original value is retained.',
        ],
        raw: copy(declaration),
      };
  } else {
    sections.staffPage = staffPage(object(declaration), uniqueNode);
    sections.dashboards = dashboards(object(declaration));
  }
  result.pins = collection(
    document,
    'capabilityReferences',
    'Saved capability references',
    'saved-pins',
    (raw, index, all) => {
      const pin = referenceRow(
          raw,
          index,
          'saved-pin',
          `Saved capability ${index + 1}`,
        ),
        key = referenceKeySignature(raw);
      if (
        key &&
        all.filter((item) => referenceKeySignature(item) === key).length > 1
      )
        pin.issues = [
          ...pin.issues,
          'Duplicate saved capability key. Every exact declaration and hash is retained in order.',
        ];
      return pin;
    },
  );
  return result;
}
